import React from 'react';
import '../styles/components/ContentCard.css';
import Icon from './Icon';
import MarkdownText from './ui/MarkdownText';
import StatusBadge, { StatusBadgeVariant } from './ui/StatusBadge';

interface ContentCardProps {
  image?: string;
  imagePlaceholder?: React.ReactNode;
  title: string;
  subtitle?: string;
  status?: {
    label: string;
    type: 'active' | 'inactive' | 'pending' | 'completed';
  };
  tags?: Array<string | { label: string; color?: string }>;
  /** A single label pinned to the card's own top-right corner, independent
   *  of the image-corner status badge (which only renders when there's an
   *  image) — a venue's floor plan name, for a card that has neither an
   *  image nor a status. */
  cornerBadge?: string;
  badges?: Array<{
    label: string;
    type: 'success' | 'warning' | 'danger' | 'info';
  }>;
  details?: Array<{
    label: string;
    value: string;
    swatch?: string;
  }>;
  pricing?: {
    amount: number;
    currency?: string;
    unit?: string;
    /** Label rendered above the amount, e.g. "Price". */
    label?: string;
    /** Hide the amount (and its label) entirely when it's exactly 0 — for
     *  cards where 0 means "not for sale"/"not set" rather than "free". */
    hideAmountIfZero?: boolean;
    stock?: {
      amount: number;
      status: 'normal' | 'low' | 'out';
      label: string;
    };
    /** Secondary line under the amount, e.g. a target cost in a different currency. */
    note?: string;
    /** Label rendered above `note`, matching `label` above the amount. */
    noteLabel?: string;
  };
  /** Itemized rows rendered above pricing — a bundle's fabrics, a fabric's
   *  variants. Capped at 4 visible rows plus an "N more" line, matching the
   *  bundle card this was lifted from. */
  lineItems?: Array<{ label: string; qty: string }>;
  /** Compact icon+label row, e.g. a venue's capacity/floor-plan/spot counts.
   *  Renders in the footer alongside (or instead of) tags/actions. */
  footerStats?: Array<{ icon: string; label: string }>;
  /** Small colored dot + label near the title — a quoted/shortlisted vendor's
   *  sentiment (positive/neutral/negative), not a full status badge. */
  sentiment?: { color: string; label: string };
  description?: string;
  /** Show at most this many lines of the description, with an ellipsis (long notes no longer stretch the card). */
  descriptionLines?: 2 | 3 | 4 | 5;
  actions?: Array<{
    icon: string;
    onClick: () => void;
    className?: string;
    title?: string;
  }>;
  customContent?: React.ReactNode;
  customActions?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  hideImage?: boolean;
}

const ContentCard: React.FC<ContentCardProps> = ({
  image,
  imagePlaceholder,
  title,
  subtitle,
  status,
  tags,
  cornerBadge,
  badges,
  details,
  pricing,
  description,
  descriptionLines,
  actions,
  customContent,
  customActions,
  className = '',
  onClick,
  hideImage = false,
  lineItems,
  footerStats,
  sentiment,
}) => {

  const getTagContent = (tag: string | { label: string; color?: string }) => {
    if (typeof tag === 'string') {
      return { label: tag, className: 'tag tag-brown', style: undefined as React.CSSProperties | undefined };
    }
    if (tag.color?.startsWith('#')) {
      return {
        label: tag.label,
        className: 'tag',
        style: { background: tag.color, color: '#fff', borderColor: tag.color } as React.CSSProperties,
      };
    }
    const colorClass = tag.color ? `tag-${tag.color}` : 'tag-brown';
    return { label: tag.label, className: `tag ${colorClass}`, style: undefined as React.CSSProperties | undefined };
  };

  const getStockClass = (status: string) => {
    switch (status) {
      case 'low': return 'stock-low';
      case 'out': return 'stock-out';
      default: return '';
    }
  };

  const getBadgeClass = (type: string) => type;

  const STATUS_VARIANT: Record<NonNullable<ContentCardProps['status']>['type'], StatusBadgeVariant> = {
    active: 'success',
    inactive: 'neutral',
    pending: 'warning',
    completed: 'success',
  };

  return (
    <div className={`content-card ${className}`} onClick={onClick}>
      {cornerBadge && <span className="card-corner-badge">{cornerBadge}</span>}
      {/* Image Section */}
      {!hideImage && image && (
        <div className="card-image">
          <img src={image} alt={title} />
          {status && (
            <StatusBadge className="card-image-badge" variant={STATUS_VARIANT[status.type]}>
              {status.label}
            </StatusBadge>
          )}
        </div>
      )}
      {!hideImage && imagePlaceholder && !image && (
        <div className="card-image">
          <div className="image-placeholder">{imagePlaceholder}</div>
          {status && (
            <StatusBadge className="card-image-badge" variant={STATUS_VARIANT[status.type]}>
              {status.label}
            </StatusBadge>
          )}
        </div>
      )}

      {/* Content Section */}
      <div className="card-content">
        {/* Date/Time if provided */}
        {details && details.find(d => d.label.toLowerCase().includes('date') || d.label.toLowerCase().includes('time')) && (
          <p className="event-datetime">
            <Icon name="calendar" size={12} />
            {details.find(d => d.label.toLowerCase().includes('date') || d.label.toLowerCase().includes('time'))?.value}
          </p>
        )}

        {/* Title */}
        <h3 className="event-gallery-title">
          {sentiment && (
            <span className="card-sentiment-dot" style={{ /* ds-dynamic: sentiment dot color from data */ background: sentiment.color }} title={sentiment.label} />
          )}
          {title}
        </h3>

        {/* Subtitle */}
        {subtitle && <p className="event-location">{subtitle}</p>}

        {/* Description */}
        <MarkdownText className={`event-desc${descriptionLines ? ` cc-clamp cc-clamp--${descriptionLines}` : ''}`}>{description}</MarkdownText>

        {/* Status Badge - shown in card-content only when image is hidden */}
        {hideImage && status && (
          <StatusBadge variant={STATUS_VARIANT[status.type]}>
            {status.label}
          </StatusBadge>
        )}

        {/* Badges */}
        {badges && badges.length > 0 && (
          <div className="card-badges">
            {badges.map((badge, index) => (
              <span key={index} className={`badge badge-${getBadgeClass(badge.type)}`}>
                {badge.label}
              </span>
            ))}
          </div>
        )}

        {/* Details Grid (exclude date/time already shown above) */}
        {details && details.length > 0 && (() => {
          const filtered = details.filter(d => {
            const label = d.label.toLowerCase();
            return !label.includes('date') && !label.includes('time');
          });
          return filtered.length > 0 ? (
            <div className="card-details">
              {filtered.map((detail, index) => (
                <div key={index} className="content-card-detail-row">
                  <span className="detail-label">{detail.label}</span>
                  <span className="detail-value">
                    {detail.swatch && (
                      <span className="detail-swatch" style={{ /* ds-dynamic: swatch color from detail data */ background: detail.swatch }} />
                    )}
                    {detail.value}
                  </span>
                </div>
              ))}
            </div>
          ) : null;
        })()}

        {/* Line items — capped at 4 rows plus an overflow count, so a long
            bundle/fabric list doesn't push the price off different cards to
            different heights within the same grid. */}
        {lineItems && lineItems.length > 0 && (
          <div className="card-line-items">
            {lineItems.slice(0, 4).map((item, index) => (
              <div key={index} className="card-line-item">
                <span>{item.label}</span>
                <span className="card-line-item-qty">{item.qty}</span>
              </div>
            ))}
            {lineItems.length > 4 && (
              <div className="card-line-item-more">+{lineItems.length - 4} more</div>
            )}
          </div>
        )}

        {/* Pricing */}
        {pricing && (
          <div className="card-pricing">
            {!(pricing.hideAmountIfZero && pricing.amount === 0) && (
              <div className="card-pricing-block">
                {pricing.label && <div className="card-pricing-label">{pricing.label}</div>}
                <div className="price-amount">
                  {pricing.currency && <span className="currency">{pricing.currency}</span>}
                  {pricing.amount.toLocaleString()}
                  {pricing.unit && <span className="unit">/{pricing.unit}</span>}
                </div>
                {pricing.stock && (
                  <div className={`stock-indicator ${getStockClass(pricing.stock.status)}`}>
                    {pricing.stock.label}
                  </div>
                )}
              </div>
            )}
            {pricing.note && (
              <div className="card-pricing-block card-pricing-block--secondary">
                {pricing.noteLabel && <div className="card-pricing-label">{pricing.noteLabel}</div>}
                <div className="card-pricing-note">{pricing.note}</div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Custom Content in Body */}
      {customContent && (
        <div className="card-custom-content">
          {customContent}
        </div>
      )}

      {/* Footer with Tags, Actions and Custom Actions */}
      {((customActions || tags) || (actions && actions.length > 0) || (footerStats && footerStats.length > 0)) && (
        <div className="content-card-footer">
          {footerStats && footerStats.length > 0 && (
            <div className="card-footer-stats">
              {footerStats.map((stat, index) => (
                <span key={index} className="card-footer-stat">
                  <Icon name={stat.icon} size={13} />
                  {stat.label}
                </span>
              ))}
            </div>
          )}
          {((customActions || tags) || (actions && actions.length > 0)) && (
          <div className="card-actions-row">
            {/* Tags on the left */}
            <div className="card-footer-left">
              {tags && tags.length > 0 && (
                <div className="card-tags">
                  {tags.map((tag, index) => {
                    const { label, className, style } = getTagContent(tag);
                    return (
                      <span key={index} className={className} style={style}>
                        {label}
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="card-custom-actions">
                {customActions}
              </div>
            </div>
            {/* Action buttons on the right */}
            {actions && actions.length > 0 && (
              <div className="card-actions">
                {actions.map((action, index) => (
                  <button
                    key={index}
                    className={`action-btn ${action.className || ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick();
                    }}
                    title={action.title}
                  >
                    <Icon name={action.icon} size={14} />
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );

};

export default ContentCard;
